import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import TypeAhead, { type TypeAheadOption } from './TypeAhead';
import LookupResultCard from '../pages/order-management/components/LookupResultCard';
import { formatCurrency } from '../utils/money';
import quoteService, {
  type QuotePickerRow,
  type QuotePickerLookupResult,
  type QuoteLookupContext,
} from '../services/quote.service';

/**
 * The V1 "Quote # / Rev #" picker (lib_editrecord.pl:9082-9152), used on the PRO
 * Invoicing tab and the Accessorial modal.
 *
 * Two ways in, one selection path:
 *   - type a quote number  -> GET /quotes/typeahead (prefix search)
 *   - click the spyglass   -> GET /quotes/lookup-for-pro (V1 `quotelookup`,
 *                             suggests the quote matching THIS pro)
 *
 * Both feed the same dropdown, so results render through one card and picking
 * one always sets quote number AND revision together — they can never drift
 * apart. Selecting writes only those two values; V1's return_quotedata
 * (js/cms.js:3002) copies no rates either.
 */

const DEBOUNCE_MS = 250;
const MIN_DIGITS = 2;

export interface QuoteLookupFieldProps {
  quoteNumber: number;
  revisionNumber: number;
  /** Fires with both halves at once; (0, 0) when cleared. */
  onChange: (quoteNumber: number, revisionNumber: number) => void;
  /** Enables the spyglass. A numeric order pro or a "T…" template pro (the
   *  lookup resolves either); omit where there is no PRO context. */
  pronumber?: number | string;
  /** The open form's match context (useOrderEditing.quoteLookupContext). V1 read
   *  every quote-match input off the form, so passing this makes the spyglass
   *  answer for STAGED edits — a rate zone or bill-to picked but not yet saved.
   *  Omit and the server falls back to the saved record for every field.
   *
   *  Partial because a caller may have only part of it: the accessorial modal sends
   *  its own `accessorialnum`/`billto` and lets the rest fall back. A field sent as
   *  '' means "the form says blank", so only send what the form actually has. */
  context?: Partial<QuoteLookupContext> | null;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

/** The stored pair as V1 displays it: "" when unset, else "NNNNNN-R". */
function formatValue(quoteNumber: number, revisionNumber: number): string {
  if (!quoteNumber) return '';
  return revisionNumber ? `${quoteNumber}-${revisionNumber}` : String(quoteNumber);
}

function toOption(row: QuotePickerRow): TypeAheadOption {
  return {
    value: `${row.quoteNumber}-${row.revisionNumber}`,
    label: row.quoteRevision || `${row.quoteNumber}-${row.revisionNumber}`,
    data: row,
  };
}

const QuoteLookupField: React.FC<QuoteLookupFieldProps> = ({
  quoteNumber,
  revisionNumber,
  onChange,
  pronumber,
  context,
  disabled = false,
  className = '',
  ariaLabel = 'Quote Number and Revision',
}) => {
  const [options, setOptions] = useState<TypeAheadOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [notice, setNotice] = useState('');
  // Free-typed text, so the input can mirror what the user is typing before they
  // pick. Null means "show the stored value" (nothing typed since the last pick).
  const [typed, setTyped] = useState<string | null>(null);

  // Bumped after a spyglass lookup so the dropdown opens: TypeAhead's freeText
  // mode only opens on type, and the spyglass supplies options without typing.
  const [openSignal, setOpenSignal] = useState(0);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against a slow earlier request landing after a newer one.
  const requestRef = useRef(0);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const runSearch = useCallback((term: string) => {
    const digits = term.replace(/\D/g, '');
    if (digits.length < MIN_DIGITS) {
      setOptions([]);
      setSearching(false);
      setNotice('');
      return;
    }
    const seq = ++requestRef.current;
    setSearching(true);
    setNotice('');
    quoteService
      .searchQuotesForPicker(digits)
      .then((rows) => {
        if (seq !== requestRef.current) return;
        setOptions(rows.map(toOption));
      })
      .catch(() => {
        if (seq !== requestRef.current) return;
        setOptions([]);
        setNotice('Quote search failed. Try again.');
      })
      .finally(() => {
        if (seq === requestRef.current) setSearching(false);
      });
  }, []);

  const handleSearch = useCallback(
    (term: string) => {
      setTyped(term);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => runSearch(term), DEBOUNCE_MS);
    },
    [runSearch],
  );

  const handleSpyglass = useCallback(() => {
    if (!pronumber) return;
    const seq = ++requestRef.current;
    setSearching(true);
    setNotice('');
    setTyped('');
    quoteService
      .lookupQuoteForPro(pronumber, context)
      .then((res: QuotePickerLookupResult) => {
        if (seq !== requestRef.current) return;
        setOptions(res.rows.map(toOption));
        // The banner doubles as the empty-state explanation, so the user can tell
        // "nothing matched these criteria" from "this PRO can't be matched".
        setNotice(res.rows.length ? res.searchCriteria : res.message || res.searchCriteria);
        setOpenSignal((n) => n + 1);
      })
      .catch(() => {
        if (seq !== requestRef.current) return;
        setOptions([]);
        setNotice('Quote lookup failed. Try again.');
        setOpenSignal((n) => n + 1);
      })
      .finally(() => {
        if (seq === requestRef.current) setSearching(false);
      });
  }, [pronumber, context]);

  const handleSelect = useCallback(
    (opt: TypeAheadOption) => {
      const row = opt.data as QuotePickerRow | undefined;
      if (!row) return;
      setTyped(null);
      setNotice('');
      setOptions([]);
      onChange(row.quoteNumber, row.revisionNumber);
    },
    [onChange],
  );

  // Typed text that was never turned into a pick is discarded — a quote
  // reference is only valid as a real quote+revision pair, and the server
  // rejects a dangling one anyway.
  const handleClose = useCallback(() => {
    setTyped(null);
    setOptions([]);
    setNotice('');
  }, []);

  const handleClear = useCallback(() => {
    setTyped(null);
    setNotice('');
    setOptions([]);
    onChange(0, 0);
  }, [onChange]);

  const renderOption = useCallback((opt: TypeAheadOption) => {
    const row = opt.data as QuotePickerRow | undefined;
    if (!row) return null;
    return (
      <LookupResultCard
        name={row.quoteRevision || `${row.quoteNumber}-${row.revisionNumber}`}
        code={row.quoteNumber}
        city={row.city}
        state={row.state}
        details={[
          { label: 'Bill To', value: row.billTo },
          { label: 'Customer', value: row.customerName },
          { label: 'Move', value: row.moveType },
          { label: 'Quote Type', value: row.quoteType },
          { label: 'Rate Zone', value: row.rateZone },
          // formatCurrency('') would read as $0.00, so guard the blank sentinel
          // and let the card drop the item.
          { label: 'Dray Rate', value: row.drayRate ? formatCurrency(row.drayRate) : '' },
          { label: 'Size', value: row.equipSize },
          { label: 'Equip', value: row.equipType },
          // V1 tints its flag columns rather than hiding expired quotes; keep the
          // row selectable and just say so.
          ...(row.expired
            ? [{ label: 'Expired', value: row.endDate || 'yes', danger: true }]
            : []),
        ]}
      />
    );
  }, []);

  return (
    <div className={`flex flex-col gap-0.5 min-w-0 ${className}`}>
      <div className="flex items-center gap-1 min-w-0">
        <TypeAhead
          freeText
          compact
          value={typed !== null ? typed : formatValue(quoteNumber, revisionNumber)}
          onChange={setTyped}
          options={options}
          onSearch={handleSearch}
          onSelect={handleSelect}
          onClose={handleClose}
          onClear={handleClear}
          openSignal={openSignal}
          searching={searching}
          // Only shown when there are no options — the criteria banner below
          // stays visible either way.
          emptyMessage={notice || `Type ${MIN_DIGITS}+ digits of a quote number`}
          renderOption={renderOption}
          disabled={disabled}
          ariaLabel={ariaLabel}
          placeholder="Quote #"
          className="flex-1 min-w-0"
        />
        {pronumber ? (
          <button
            type="button"
            onClick={handleSpyglass}
            disabled={disabled}
            aria-label="Find the quote for this PRO"
            title="Find Quote — match this PRO's bill-to, rate zone and delivery location"
            className={
              'inline-flex items-center justify-center h-6 w-6 shrink-0 rounded ' +
              'text-blue-600 dark:text-blue-400 hover:bg-slate-100 dark:hover:bg-gray-800 ' +
              'disabled:opacity-40 disabled:cursor-not-allowed'
            }
          >
            <MagnifyingGlassIcon className="w-4 h-4" />
          </button>
        ) : null}
      </div>
      {notice ? (
        // V1's search-criteria header (main.pl:7157), or the reason there is
        // nothing to show. Cleared as soon as a quote is picked.
        <p role="status" className="text-[0.6875rem] leading-tight text-slate-500 dark:text-slate-400">
          {notice}
        </p>
      ) : null}
    </div>
  );
};

export default QuoteLookupField;
